import { Link } from 'react-router-dom';
import { EmptyState } from '@/components/EmptyState';
import { CapacityBar } from '@/components/dashboard/CapacityBar';
import type { EndpointCapacityItem } from '@/lib/dashboardCompute';
import { formatGb } from '@/lib/format';

export interface EndpointCapacityPanelProps {
  items: EndpointCapacityItem[];
}

const maxPercent = (i: EndpointCapacityItem) => Math.max(i.cpu.percent, i.memory.percent, i.storage.percent);

/** Per-endpoint capacity bars, fullest first. Wrap in a Panel on the page. */
export function EndpointCapacityPanel({ items }: EndpointCapacityPanelProps) {
  if (items.length === 0) return <EmptyState as="h3" title="No endpoints" className="border-0 py-6" />;

  // Array.prototype.sort is stable (ES2019+), so ties keep their original order.
  const sorted = [...items].sort((a, b) => {
    if (a.hasResources !== b.hasResources) return a.hasResources ? -1 : 1;
    return maxPercent(b) - maxPercent(a);
  });

  return (
    <div className="space-y-4">
      <ul className="space-y-5">
        {sorted.map((item) => (
          <li key={item.id} className="min-w-0 space-y-2">
            <Link
              to={`/resources/endpoints/${item.id}`}
              title={item.name}
              className="block truncate rounded-sm font-medium outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
            >
              {item.name}
            </Link>
            {item.hasResources ? (
              <div className="grid gap-4 sm:grid-cols-3">
                <CapacityBar label="CPU" unit="cores" {...item.cpu} />
                <CapacityBar label="Memory" unit="GB" format={formatGb} {...item.memory} />
                <CapacityBar label="Storage" unit="GB" {...item.storage} />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Capacity not configured.</p>
            )}
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground">Shared-CPU stacks are exempt from CPU reservation.</p>
    </div>
  );
}
